import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuthStore } from '../store/authStore';

interface PrivateRouteProps {
  requiredPermission?: string;
  requiredRole?: string[];
  requiredPosition?: string[];
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ 
  requiredPermission, 
  requiredRole,
  requiredPosition
}) => {
  const location = useLocation();
  const { isAuthenticated, isLoading, user, refreshUser } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated && user) {
      refreshUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" tip="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  if (requiredRole && user && !requiredRole.includes((user.role ?? user.roleName ?? '') as string)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check permission-based access
  if (requiredPermission && user) {
    const hasPermission = 
      (user.role ?? user.roleName) === 'super-admin' || 
      (user.permissions?.includes(requiredPermission) ?? false);
    
    if (!hasPermission) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Check position-based access
  if (requiredPosition && user && !requiredPosition.includes((user.position ?? user.positionName ?? '') as string)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <Outlet />;
};

export default PrivateRoute;
